import {
  ActivityKindEnum,
  AdFieldBathsEnum,
  AdFieldBedsEnum,
  AdFieldCurrencyEnum,
  AdFieldFloorEnum,
  AdFieldFurnitureEnum,
  AdFieldMultilistingEnum,
  AdFieldOccupancyEnum,
  AdFieldRenovationEnum,
  AdFieldStatusEnum,
  AdFieldVisibilityEnum,
  CompletionStatusEnum,
  CurrencyCommissionEnum,
  PropertyKindEnum,
  RentPeriodEnum,
  TransactionTypeEnum,
} from './enums';
import type { AdCreatorType, AdMediaType, HandoverType } from './ads';

export const PROPERTY_AD_IN_PROGRESS_STATUS = 'inProgress' as const;

export interface PropertyAdAmenityValues {
  balcony: boolean;
  maidsRoom: boolean;
  builtInWardrobes: boolean;
  childrenPlayArea: boolean;
  sharedPool: boolean;
  sharedSpa: boolean;
  sharedGym: boolean;
  study: boolean;
  centralAc: boolean;
  concierge: boolean;
  coveredParking: boolean;
  barbecueArea: boolean;
  petsAllowed: boolean;
  publicParking: boolean;
  kitchenAppliances: boolean;
  lobbyInBuilding: boolean;
  maidService: boolean;
  petsNotAllowed: boolean;
  privateGarden: boolean;
  privateGym: boolean;
  privatePool: boolean;
  security: boolean;
  tennisCourts: boolean;
  privateSpa: boolean;
  laundryFacility: boolean;
}

export type PropertyAdAmenityField = keyof PropertyAdAmenityValues;

type PropertyAdInProgressAmenityValues = {
  [Field in PropertyAdAmenityField]?: boolean | null;
};

export interface PropertyAdInProgressWorkspace extends PropertyAdInProgressAmenityValues {
  id: string;
  propertyId: string;
  status: typeof PROPERTY_AD_IN_PROGRESS_STATUS;
  version: number;
  createdByUserId: string;
  userId?: string | null;
  propertyAdCreator?: AdCreatorType | null;
  activityKind?: ActivityKindEnum | null;
  transactionType?: TransactionTypeEnum | null;
  defaultRentFrequency?: RentPeriodEnum | null;
  propertyKind?: PropertyKindEnum | null;
  price?: number | null;
  sellingPrice?: number | null;
  completionStatus?: CompletionStatusEnum | null;
  toSeller?: number | null;
  toDeveloper?: number | null;
  postHandover?: boolean | null;
  handover?: HandoverType | null;
  priceYearly?: number | null;
  priceMonthly?: number | null;
  priceWeekly?: number | null;
  priceDaily?: number | null;
  currency?: AdFieldCurrencyEnum | null;
  addressEn?: string | null;
  nameEn?: string | null;
  title?: string;
  placeId?: string | null;
  coordinatesLat?: string | null;
  coordinatesLong?: string | null;
  locationId?: string | null;
  districtId?: string | null;
  buildingName?: string | null;
  multilisting?: AdFieldMultilistingEnum | null;
  addCommission?: string | null;
  currencyCommission?: CurrencyCommissionEnum | null;
  topUp?: boolean;
  topUpConditions?: string | null;
  description?: string | null;
  builtUpArea?: number | null;
  beds?: AdFieldBedsEnum | null;
  baths?: AdFieldBathsEnum | null;
  floor?: AdFieldFloorEnum | null;
  furniture?: AdFieldFurnitureEnum | null;
  renovation?: AdFieldRenovationEnum | null;
  occupancy?: AdFieldOccupancyEnum | null;
  rentedTill?: string | null;
  view?: string[] | null;
  propertyAdMedia?: AdMediaType[] | null;
}

export interface PropertyAdMediaUpdateRequest {
  propertyAdMedia: AdMediaType[];
}

export interface PropertyAdEditorFormValues extends PropertyAdAmenityValues {
  responsibleUserId: string | null;
  activityKind: ActivityKindEnum | null;
  transactionType: TransactionTypeEnum | null;
  defaultRentFrequency: RentPeriodEnum | null;
  propertyKind: PropertyKindEnum | null;
  price: number | null;
  sellingPrice: number | null;
  completionStatus: CompletionStatusEnum | null;
  toSeller: number | null;
  toDeveloper: number | null;
  postHandover: boolean | null;
  handover: HandoverType | null;
  priceYearly: number | null;
  priceMonthly: number | null;
  priceWeekly: number | null;
  priceDaily: number | null;
  currency: AdFieldCurrencyEnum;
  addressEn: string | null;
  nameEn: string | null;
  title: string;
  placeId: string | null;
  coordinatesLat: string | null;
  coordinatesLong: string | null;
  locationId: string | null;
  districtId: string | null;
  buildingName: string | null;
  multilisting: AdFieldMultilistingEnum | null;
  addCommission: number | null;
  currencyCommission: CurrencyCommissionEnum | null;
  topUp: boolean;
  topUpConditions: string;
  description: string;
  builtUpArea: number | null;
  beds: AdFieldBedsEnum | null;
  baths: AdFieldBathsEnum | null;
  floor: AdFieldFloorEnum | null;
  furniture: AdFieldFurnitureEnum | null;
  renovation: AdFieldRenovationEnum | null;
  occupancy: AdFieldOccupancyEnum | null;
  rentedTill: string | null;
  view: string[];
}

type PropertyAdEditorAutosaveFormValues = PropertyAdAmenityValues &
  Pick<
    PropertyAdEditorFormValues,
    | 'responsibleUserId'
    | 'activityKind'
    | 'transactionType'
    | 'propertyKind'
    | 'currency'
    | 'price'
    | 'sellingPrice'
    | 'completionStatus'
    | 'toSeller'
    | 'toDeveloper'
    | 'postHandover'
    | 'handover'
    | 'defaultRentFrequency'
    | 'priceYearly'
    | 'priceMonthly'
    | 'priceWeekly'
    | 'priceDaily'
    | 'addressEn'
    | 'nameEn'
    | 'title'
    | 'placeId'
    | 'coordinatesLat'
    | 'coordinatesLong'
    | 'locationId'
    | 'districtId'
    | 'buildingName'
    | 'multilisting'
    | 'addCommission'
    | 'currencyCommission'
    | 'topUp'
    | 'topUpConditions'
    | 'description'
    | 'builtUpArea'
    | 'beds'
    | 'baths'
    | 'floor'
    | 'furniture'
    | 'renovation'
    | 'occupancy'
    | 'rentedTill'
    | 'view'
  >;

export type PropertyAdEditorAutosaveValues = Omit<
  PropertyAdEditorAutosaveFormValues,
  'currency' | 'description' | 'topUpConditions'
> & {
  currency: AdFieldCurrencyEnum | null;
  description: string | null;
  topUpConditions: string | null;
};

export type PropertyAdInProgressAutosaveFields = Partial<
  Omit<PropertyAdEditorAutosaveValues, 'addCommission'>
> & {
  addCommission?: string | null;
};

export type PropertyAdInProgressAutosaveRequest = PropertyAdInProgressAutosaveFields & {
  expectedVersion: number;
};

export type PropertyAdEditorEditRequest = Omit<
  PropertyAdEditorFormValues,
  'addCommission' | 'responsibleUserId'
> & {
  addCommission: string | null;
  creatorId: string;
  fromXml: boolean;
  propertyAdAnnouncerIds: [];
  propertyAdMedia: AdMediaType[];
  publishedAt?: string;
  status: AdFieldStatusEnum;
  visibility: AdFieldVisibilityEnum;
};
